import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ACTIVATE_IMMEDIATELY, END_NUMBER, formatDateTime, formatRangeNumber, isRangeActive, RANGE_DESCRIPTION, START_NUMBER, WARNING_PERCENTAGE } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { rangeSchema } from "./validations";
import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { NumberRangesSectionProps } from "./types";

const useNumberRangesSection = ({onAddRange}: Pick<NumberRangesSectionProps, "onAddRange">) => {
  const [open, setOpen] = useState(false);
  
  const form = useForm({
    defaultValues: {
      [START_NUMBER]: "",
      [END_NUMBER]: "",
      [RANGE_DESCRIPTION]: "",
      [WARNING_PERCENTAGE]: "90",
    },
    resolver: zodResolver(rangeSchema),
  });

  const onOpenAdd = () => {
    form.reset({
      [START_NUMBER]: "",
      [END_NUMBER]: "",
      [RANGE_DESCRIPTION]: "",
      [WARNING_PERCENTAGE]: "90",
    });
    setOpen(true);
  };

  const onSave = form.handleSubmit((data) => {
    onAddRange({...data, [ACTIVATE_IMMEDIATELY]: true});
    setOpen(false);
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "startNumber",
        header: "Start Number",
        accessorFn: (row: any) => formatRangeNumber(row?.startNumber ?? row?.startRange),
      },
      {
        accessorKey: "endNumber",
        header: "End Number",
        accessorFn: (row: any) => formatRangeNumber(row?.endNumber ?? row?.endRange),
      },
      {
        accessorKey: "lastIssuedNumber",
        header: "Last Number Used",
        accessorFn: (row: any) => formatRangeNumber(row?.lastIssuedNumber),
      },
      {
        accessorKey: "nextNumber",
        header: "Next Number",
        accessorFn: (row: any) => formatRangeNumber(row?.nextNumber),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }: any) => {
          const active = isRangeActive(row?.original);
          return (
            <Badge
              variant="outline"
              className={cn(
                "rounded-full px-3 py-1",
                active
                  ? "bg-green-50 border-green-500 text-green-600"
                  : "bg-gray-50 border-gray-400 text-gray-600"
              )}
            >
              {active ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        accessorFn: (row: any) => row?.description || "—",
      },
      {
        accessorKey: "activatedAt",
        header: "Activated",
        accessorFn: (row: any) => formatDateTime(row?.activatedAt || row?.activated || row?.createdAt),
      },
      {
        accessorKey: "deactivatedAt",
        header: "Deactivated",
        accessorFn: (row: any) => formatDateTime(row?.deactivatedAt || row?.deactivated),
      },
    ],
    []
  );

  return {
    form,
    columns,
    onOpenAdd,
    onSave,
    open,
    setOpen,
  };
};

export default useNumberRangesSection;